import React, { useContext, useEffect, useMemo, useState } from "react";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Save, ArrowLeft, User } from "lucide-react";

const API = "https://nissi-india.com/api/companyMaster";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const CompanyContactAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [params] = useSearchParams();

  // ASPX: company always 1
  const companyId = 1;
  const contactId = Number(params.get("contactId") || 0);
  const isEdit = contactId > 0;

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    contactName: "",
    designation: "",
    phoneNo: "",
    mobile: "",
    email: "",
    remarks: "",
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pageTitle = useMemo(
    () => (isEdit ? "COMPANY CONTACT UPDATE" : "COMPANY CONTACT ADD"),
    [isEdit]
  );

  const loadContact = async () => {
    if (!isEdit) return;
    const r = await axios.get(`${API}/company/${companyId}/contact/${contactId}`, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });
        const d = r.data.data;

    setForm({
      contactName: d.fld_contactname || "",
      designation: d.fld_designation || "",
      phoneNo: d.fld_phoneno || "",
      mobile: d.fld_mobile || "",
      email: d.fld_email || "",
      remarks: d.fld_remarks || "",
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadContact();
      } catch (e) {
        console.error(e);
        toast.error("Failed to load contact details");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const validateForm = () => {
    if (!form.contactName.trim()) return toast.error("Please enter Contact Name"), false;

    const email = form.email.trim();
    if (email) {
      // ASPX pattern: tld 2-4
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      if (!emailPattern.test(email)) return toast.error("Please enter valid emailid"), false;
    }

    const phone = form.phoneNo.trim();
    if (phone) {
      const allowed = "0123456789()-, ";
      for (let ch of phone) {
        if (!allowed.includes(ch)) return toast.error("Please Enter a valid Phone Number"), false;
      }
    }

    return true;
  };

  const onSave = async () => {
    try {
      if (!validateForm()) return;

      const payload = {
        isUpdate: isEdit,
        contactId,
        contactName: form.contactName,
        designation: form.designation,
        phoneNo: form.phoneNo,
        mobile: form.mobile.replace(/[^\d]/g, "").slice(0, 10),
        email: form.email,
        remarks: form.remarks,
      };

      const r = await axios.post(`${API}/company/${companyId}/contact/save`, payload, {
        headers: tenantHeaders(),
      });
      if (r.data.ok) {
        toast.success(isEdit ? "Updated Successfully" : "Inserted Successfully");
        navigate("/adminMaster/CompanyMaster?tab=contact");
      } else {
        toast.error("Save failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Save failed");
    }
  };

  return (
    <AdminLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{pageTitle}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top buttons */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={() => navigate("/adminMaster/CompanyMaster?tab=contact")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
                isDark
                  ? "border-white/10 bg-white/5 hover:bg-white/10 text-white"
                  : "border-gray-200 bg-gray-50 hover:bg-white text-gray-900"
              } transition`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={onSave}
              disabled={loading}
              className={`
                flex items-center gap-2
                px-5 py-2 rounded-xl
                text-white font-semibold
                shadow-lg transition-all duration-200
                ${buttonClass} ${hoverClass}
                ${loading ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-0.5"}
              `}
            >
              <Save className="w-4 h-4" />
              {isEdit ? "Update" : "Submit"}
            </button>
          </div>

          {/* Form */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Contact Name *</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                <input
                  className="w-full mt-1 p-2 pl-9 rounded-lg border"
                  value={form.contactName}
                  onChange={(e) => setField("contactName", e.target.value)}
                  placeholder="Enter contact name"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold">Designation</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.designation}
                onChange={(e) => setField("designation", e.target.value)}
                placeholder="Designation"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Phone No</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                maxLength={11}
                value={form.phoneNo}
                onChange={(e) => setField("phoneNo", e.target.value)}
                placeholder="Phone (allowed: 0-9 () - , space)"
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Mobile</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                maxLength={10}
                value={form.mobile}
                onChange={(e) => setField("mobile", e.target.value.replace(/[^\d]/g, ""))}
                placeholder="10 digit mobile"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Email</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="Email"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Remarks</label>
              <textarea
                className="w-full mt-1 p-2 rounded-lg border"
                rows={3}
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
                placeholder="Remarks"
              />
            </div>
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CompanyContactAdd;
